/**
 * Hook zur Berechnung des Kalender-Layouts.
 * Orchestrates basic day generation, grouping, stacking and conflict detection.
 */
import { useMemo } from 'react';
import { 
    getWeekNumber, isSameDay, toDateKey, toLocalISO 
} from '../../../../../utils/dateUtils';
import { MONTH_NAMES } from '../constants';
import { useAssignmentGrouping } from './useAssignmentGrouping';
import { useAllDayStacking } from './useAllDayStacking';
import { useCalendarConflicts } from './useCalendarConflicts';

export function useCalendarLayout({
    viewMode,
    startDate,
    viewDays,
    assignments,
    isSidebarOpen,
    interaction,
    assignmentsForConflicts,
    absencesForConflicts,
    workers = [],
    vehicles = [],
    kolonnen = [],
    today = new Date()
}) {
    // 1. Normalisiere heute
    const todayNormalized = useMemo(() => {
        const d = new Date(today);
        d.setHours(0, 0, 0, 0);
        return d;
    }, [today]);

    // 2. Array der sichtbaren Tage berechnen
    const days = useMemo(() => {
        if (viewMode === 'month') {
            const year = startDate.getFullYear();
            const month = startDate.getMonth();
            const first = new Date(year, month, 1);
            const last = new Date(year, month + 1, 0);
            const result = [];
            for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
                result.push(new Date(d));
            }
            return result;
        }
        const result = [];
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        for (let i = 0; i < viewDays; i++) {
            const d = new Date(start);
            d.setDate(d.getDate() + i);
            result.push(d);
        }
        return result;
    }, [startDate, viewDays, viewMode]);

    // 3. Datum-Label für den Header
    const dateLabel = useMemo(() => {
        if (viewMode === 'month') {
            return `${MONTH_NAMES[startDate.getMonth()]} ${startDate.getFullYear()}`;
        }
        if (days.length === 0) return '';
        const first = days[0];
        const last = days[days.length - 1];
        const kw = getWeekNumber(first);
        const fmtDate = (d) => `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
        
        if (isSameDay(first, last)) {
            return `KW ${kw} · ${fmtDate(first)}.${first.getFullYear()}`;
        }
        const yearSuffix = isSidebarOpen ? '' : `.${last.getFullYear()}`;
        return `KW ${kw} · ${fmtDate(first)} – ${fmtDate(last)}${yearSuffix}`;
    }, [days, viewMode, startDate, isSidebarOpen]);

    const showsToday = useMemo(() => days.some(d => isSameDay(d, todayNormalized)), [days, todayNormalized]);

    // 4. Sub-Hooks for logic extraction
    const { allDayByKey, timedByKey, maxAllDayCount } = useAssignmentGrouping(days, assignments);
    
    const allDayLayouts = useAllDayStacking(days, allDayByKey, interaction);

    const assignmentConflictsMap = useCalendarConflicts({
        assignments, assignmentsForConflicts, absencesForConflicts, workers, vehicles, kolonnen
    });

    const monthDragPreview = useMemo(() => {
        if (viewMode !== 'month' || interaction?.type !== 'month-move') {
            return { previewAssignments: [], previewDayKeys: new Set(), draggedAssignmentId: null };
        }
        const draggedAssignment = interaction.assignment;
        if (!draggedAssignment?.id || !draggedAssignment.start_datetime || !interaction.targetDay) {
            return { previewAssignments: [], previewDayKeys: new Set(), draggedAssignmentId: draggedAssignment?.id || null };
        }

        const start = new Date(draggedAssignment.start_datetime);
        const end = new Date(draggedAssignment.end_datetime || draggedAssignment.start_datetime);
        const sourceDay = new Date(interaction.sourceDay || interaction.currentDay || start);
        const targetDay = new Date(interaction.targetDay || sourceDay);
        const durationDays = Math.max(0, Math.round((new Date(end).setHours(0, 0, 0, 0) - new Date(start).setHours(0, 0, 0, 0)) / 86400000));
        const shiftDays = Math.round((targetDay.setHours(0, 0, 0, 0) - sourceDay.setHours(0, 0, 0, 0)) / 86400000);
        const previewStart = new Date(start);
        previewStart.setHours(0, 0, 0, 0);
        previewStart.setDate(previewStart.getDate() + shiftDays);

        const previewAssignments = [];
        const previewDayKeys = new Set();

        for (let offset = 0; offset <= durationDays; offset++) {
            const previewDay = new Date(previewStart);
            previewDay.setDate(previewDay.getDate() + offset);
            previewDay.setHours(0, 0, 0, 0);
            previewDayKeys.add(toDateKey(previewDay));

            previewAssignments.push({
                ...draggedAssignment,
                id: `ghost-${draggedAssignment.id}-${toDateKey(previewDay)}`,
                isGhost: true,
                start_datetime: toLocalISO(previewDay),
                end_datetime: toLocalISO(previewDay)
            });
        }

        return { previewAssignments, previewDayKeys, draggedAssignmentId: draggedAssignment.id };
    }, [interaction, viewMode]);

    return {
        days, dateLabel, showsToday,
        allDayByKey, timedByKey, maxAllDayCount,
        allDayLayouts, assignmentConflictsMap, monthDragPreview
    };
}
